import React, { useMemo, useState } from 'react';
import { Card, CardContent, Typography, Box, Button, ButtonGroup, TextField } from '@material-ui/core';
import { ArrowUpward } from '@material-ui/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { SimovsatDeviceDistance } from '~/types';
import { parse as dateFnsParse, isAfter, isBefore, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, parse, format } from 'date-fns';

interface DistanceChartProps {
  deviceDistance: SimovsatDeviceDistance;
}

const COLORS = [
  '#424242',
  '#2196F3',
  '#4CAF50',
  '#9C27B0',
  '#E0E0E0',
  '#F44336',
  '#3F51B5',
];

// A API retorna datas no formato "May 11" — tenta parsear para comparar com filtros
const parseDateLabel = (label: string): Date => {
  try {
    // Tenta "MMM dd" com ano corrente
    const year = new Date().getFullYear();
    return dateFnsParse(`${label} ${year}`, 'MMM dd yyyy', new Date());
  } catch {
    return new Date();
  }
};

type FilterType = 'day' | 'week' | 'fortnight' | 'month' | 'custom';

export default function DistanceChart({ deviceDistance }: DistanceChartProps) {
  const [filterType, setFilterType] = useState<FilterType>('week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [refDate, setRefDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [refMonth, setRefMonth] = useState(format(new Date(), 'yyyy-MM'));

  const { datas, por_entidade } = deviceDistance;
  const entities = Object.keys(por_entidade);

  // Monta os dados para o gráfico: cada ponto é uma data com km por entidade
  const chartData = useMemo(() => {
    if (!datas || datas.length === 0) return [];

    // Determinar intervalo de datas pelo filtro
    const now = new Date();
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    const referenceDate = refDate ? dateFnsParse(refDate, 'yyyy-MM-dd', new Date()) : now;

    switch (filterType) {
      case 'day':
        startDate = startOfDay(referenceDate);
        endDate = endOfDay(referenceDate);
        break;
      case 'week':
        startDate = subDays(referenceDate, 6);
        endDate = endOfDay(referenceDate);
        break;
      case 'fortnight':
        startDate = subDays(referenceDate, 14);
        endDate = endOfDay(referenceDate);
        break;
      case 'month':
        if (refMonth) {
          const monthDate = parse(refMonth, 'yyyy-MM', new Date());
          startDate = startOfMonth(monthDate);
          endDate = endOfMonth(monthDate);
        } else {
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
        }
        break;
      case 'custom':
        if (customStart) startDate = startOfDay(dateFnsParse(customStart, 'yyyy-MM-dd', new Date()));
        if (customEnd) endDate = endOfDay(dateFnsParse(customEnd, 'yyyy-MM-dd', new Date()));
        break;
      default:
        startDate = subDays(now, 6);
        endDate = endOfDay(now);
    }

    // Filtrar índices de datas que estão no intervalo
    const validIndices = datas.reduce<number[]>((acc, label, idx) => {
      const d = parseDateLabel(label);
      if (startDate && isBefore(startOfDay(d), startOfDay(startDate))) return acc;
      if (endDate && isAfter(startOfDay(d), startOfDay(endDate))) return acc;
      acc.push(idx);
      return acc;
    }, []);

    return validIndices.map(idx => {
      const point: Record<string, any> = { data: datas[idx] };
      entities.forEach(entity => {
        const pairs = por_entidade[entity] || [];
        const pair = pairs.find(p => p[0] === idx);
        point[entity] = pair ? pair[1] : 0;
      });
      return point;
    });
  }, [deviceDistance, filterType, customStart, customEnd, refDate, refMonth]);

  return (
    <Card>
      <CardContent>
        <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' style={{ marginBottom: 16 }}>
          <Box display='flex' alignItems='center' style={{ gap: 8 }}>
            <Typography variant='h6' style={{ fontWeight: 'bold' }}>
              DISTÂNCIA PERCORRIDA
            </Typography>
            <ArrowUpward style={{ color: '#4CAF50' }} />
          </Box>

          <Box display='flex' alignItems='center' flexWrap='wrap' style={{ gap: 10 }}>
            <ButtonGroup size="small" aria-label="filtros de período">
              <Button
                color={filterType === 'day' ? 'primary' : 'default'}
                variant={filterType === 'day' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('day')}
              >
                Dia
              </Button>
              <Button
                color={filterType === 'week' ? 'primary' : 'default'}
                variant={filterType === 'week' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('week')}
              >
                Semana
              </Button>
              <Button
                color={filterType === 'fortnight' ? 'primary' : 'default'}
                variant={filterType === 'fortnight' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('fortnight')}
              >
                Quinzena
              </Button>
              <Button
                color={filterType === 'month' ? 'primary' : 'default'}
                variant={filterType === 'month' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('month')}
              >
                Mês
              </Button>
              <Button
                color={filterType === 'custom' ? 'primary' : 'default'}
                variant={filterType === 'custom' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('custom')}
              >
                Entre datas
              </Button>
            </ButtonGroup>
          </Box>
        </Box>

        <Box display='flex' justifyContent='flex-end' alignItems='center' style={{ marginBottom: 16, gap: 16 }}>
          {filterType === 'custom' && (
            <>
              <TextField
                label="Data Inicial"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="Data Final"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </>
          )}

          {(filterType === 'day' || filterType === 'week' || filterType === 'fortnight') && (
            <TextField
              label={filterType === 'day' ? "Data" : "Data de referência"}
              type="date"
              value={refDate}
              onChange={(e) => setRefDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          )}

          {filterType === 'month' && (
            <TextField
              label="Mês de referência"
              type="month"
              value={refMonth}
              onChange={(e) => setRefMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          )}
        </Box>

        {chartData.length === 0 ? (
          <Typography variant='body2' style={{ textAlign: 'center', color: '#666', padding: 40 }}>
            Não há dados disponíveis para o período selecionado
          </Typography>
        ) : (
          <ResponsiveContainer width='100%' height={500}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 20, bottom: 120 }}
            >
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='data'
                angle={-45}
                textAnchor='end'
                height={120}
                interval={0}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                label={{ value: 'Distância (Km)', angle: -90, position: 'insideLeft' }}
                domain={[0, 'auto']}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: number, name: string) => [`${Number(value).toFixed(2)} Km`, name]}
                labelFormatter={(label) => `Data: ${label}`}
                contentStyle={{ fontSize: '12px' }}
              />
              <Legend
                wrapperStyle={{ paddingTop: 10 }}
                iconType='square'
                formatter={(value: string) => {
                  const maxLength = 20;
                  return value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
                }}
                style={{ fontSize: '10px' }}
              />
              {entities.map((entity, index) => (
                <Bar
                  key={entity}
                  dataKey={entity}
                  stackId='a'
                  fill={COLORS[index % COLORS.length]}
                  name={entity}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
